<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :close-on-click="false"
    max-width="600"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        v-if="!iconOnly"
        :value="dateRangeText"
        :class="whiteTheme ? 'white-theme' : ''"
        readonly
        clearable
        :prepend-icon="noIcon ? '' : 'mdi-calendar-range'"
        v-on="on"
        @click:clear="clear"
      ></v-text-field>
      <v-icon v-else color="white" v-on="on" @click:clear="clear">
        mdi-calendar-range
      </v-icon>
    </template>
    <v-date-picker
      v-model="dates"
      range
      full-width
      :landscape="$vuetify.breakpoint.smAndUp"
      header-color="primary"
      class="date-time-range-picker"
    >
      <v-row justify="space-around" dense>
        <v-col md="4">
          <v-text-field
            v-model="startTime"
            label="Start Time"
            type="time"
            prepend-icon="mdi-clock-start"
            dense
            :error="startErr"
            :error-messages="errorMessage"
            @click:prepend="
              mode = 0;
              dialog = true;
            "
            @click:clear="startErr = false"
          ></v-text-field>
        </v-col>
        <v-col md="4">
          <v-text-field
            v-model="endTime"
            label="End Time"
            type="time"
            prepend-icon="mdi-clock-end"
            dense
            :error="endErr"
            :error-messages="errorMessage"
            @click:prepend="
              mode = 1;
              dialog = true;
            "
            @click:clear="endErr = false"
          ></v-text-field>
        </v-col>
      </v-row>
      <div class="d-flex justify-end" style="width:100%;">
        <v-btn text color="primary" @click="cancel">Cancel</v-btn>
        <v-btn dark tile color="third" @click="setRange">Apply</v-btn>
      </div>
      <div
        :class="{ shrinkHeight: rangeLimited }"
        class="shortcuts"
        style="display: flex !important; flex-direction: column !important"
      >
        <v-btn
          v-for="(btn, i) in rangeShortcuts"
          :key="i"
          depressed
          style="margin-bottom: 5px"
          color="third white--text"
          class="ml-0"
          @click="() => quickTime(btn)"
          >{{ btn }}</v-btn
        >
      </div>
    </v-date-picker>

    <v-dialog
      ref="dialog"
      v-model="dialog"
      :return-value.sync="time"
      transition="scale-transition"
      persistent
      width="290px"
    >
      <v-time-picker
        ref="timePicker"
        v-model="time"
        header-color="third"
        full-width
        scrollable
        ampm-in-title
      >
        <v-spacer></v-spacer>
        <div class="control-container">
          <v-btn text color="primary" @click="dialog = false">Discard</v-btn>
          <v-btn text color="primary" @click="setTime">SAVE</v-btn>
        </div>
      </v-time-picker>
    </v-dialog>
  </v-menu>
</template>

<script>
import { Logger } from "aws-amplify";
const logger = new Logger("Date Time Range");
import moment from "moment";

export default {
  name: 'CalendarSelection',
  props: {
    startValue: {
      type: [Array, Boolean],
      required: false,
      default: () => false,
    },
    iconOnly: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    rangeLimited: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    // true if you do not want the selected values to be converted to utc
    localTime: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    noIcon: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    whiteTheme: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data: () => ({
    shortcuts: [
      "Today",
      "Yesterday",
      "Last 7 Days",
      "Last 30 Days",
      "This Month",
      "Last Month",
    ],
    result: [],
    dates: [],
    menu: false,
    dialog: false,
    time: null,
    mode: 0,
    startTime: null,
    endTime: null,
    endErr: false,
    startErr: false,
    errorMessage: [],
  }),
  computed: {
    dateRangeText() {
      logger.debug(this.dates, this.startTime, this.endTime);
      if (!this.dates || !this.dates.length) return "";

      // we sort sort our dates, earliest first, format them, and create a set so the same values are not displayed twice.
      let date = [
        ...new Set(
          this.dates
            .map((d) =>
              this.localTime
                ? moment(d, "YYYY-MM-DDTHH:mm:ss").format("D MMM YYYY")
                : moment
                    .utc(d)
                    .local()
                    .format("D MMM YYYY")
            )
            .sort((a, b) => (moment(a, "LL").isBefore(b, "LL") ? -1 : 1))
        ),
      ];

      return date.join(" - ");
    },
    rangeShortcuts() {
      if (this.rangeLimited) {
        return ["Today", "Yesterday", "Last 7 Days"];
      } else {
        return [
          "Today",
          "Yesterday",
          "Last 7 Days",
          "Last 30 Days",
          "This Month",
          "Last Month",
        ];
      }
    },
  },
  watch: {
    startValue(val) {
      this.dates = val.map((date) => moment(date).format("YYYY-MM-DD"));
      this.change();
    },
    endTime: "change",
    dates: "change",
  },
  mounted() {
    if (this.startValue) {
      this.dates = this.startValue.map((date) =>
        moment(date).format("YYYY-MM-DD")
      );
    }
  },
  methods: {
    cancel() {
      this.dates = [];
      this.startErr = false;
      this.endErr = false;
      this.startTime = null;
      this.endTime = null;
      this.errorMessage = [];
      this.menu = false;
    },
    clear() {
      this.$emit("change", false);
      this.dates = [];
    },
    change() {
      this.errorMessage = [];
      if (this.startErr) this.startErr = false;
      if (this.endErr) this.endErr = false;
    },
    setTime() {
      logger.debug(
        this.$refs,
        this.mode,
        this.time,
        this.startTime,
        this.endTime
      );
      if (this.mode) {
        this.endTime = this.time;
      } else {
        this.startTime = this.time;
      }
      this.$refs.timePicker.selecting = 1;
      this.dialog = false;
    },
    setRange() {
      if (!this.dates || this.dates.length === 0) {
        this.dates = this.startValue;
      }
      const dates =
        typeof this.dates === "string"
          ? [this.dates]
          : Array.from(this.dates).sort();
      if (dates.length === 1) dates.push(dates[0]);

      if (!this.startTime && !this.endTime) {
        dates[0] += " 00:00:00";
        dates[1] += " 23:59:59";
      } else if (!this.startTime) {
        this.startErr = true;
        this.errorMessage = "start time is not valid";
        this.$emit("change", false);
        return;
      } else if (!this.endTime) {
        this.endErr = true;
        this.errorMessage = "end time is not valid";
        this.$emit("change", false);
        return;
      } else if (dates[0] === dates[1] && this.startTime >= this.endTime) {
        this.startErr = true;
        this.endErr = true;
        this.errorMessage = "start time is greater than end time";
        return;
      } else {
        dates[0] += ` ${this.startTime}`;
        dates[1] += ` ${this.endTime}`;
      }

      const res = dates.map((val) => {
        const res = this.localTime
          ? moment(val).format("YYYY-MM-DDTHH:mm:ss")
          : moment(val)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss");
        return res; //.substring(0, res.length - 1);
      });

      this.$emit("change", res);
      this.menu = false;
    },
    quickTime(btn) {
      logger.debug(btn);
      let res = "";
      switch (btn.toUpperCase()) {
        case "TODAY":
          res = [
            moment()
              .startOf("day")
              .format("YYYY-MM-DDTHH:mm:ss"),
            moment()
              .endOf("day")
              .format("YYYY-MM-DDTHH:mm:ss"),
          ];
          break;
        case "YESTERDAY":
          res = [
            moment()
              .subtract(1, "days")
              .startOf("day")
              .format("YYYY-MM-DDTHH:mm:ss"),
            moment()
              .subtract(1, "days")
              .endOf("day")
              .format("YYYY-MM-DDTHH:mm:ss"),
          ];
          break;
        case "LAST 7 DAYS":
          res = [
            moment()
              .subtract(7, "days")
              .startOf("day")
              .format("YYYY-MM-DDTHH:mm:ss"),
            moment()
              .endOf("day")
              .format("YYYY-MM-DDTHH:mm:ss"),
          ];
          break;

        case "LAST 30 DAYS":
          res = [
            moment()
              .subtract(30, "days")
              .startOf("day")
              .format("YYYY-MM-DDTHH:mm:ss"),
            moment()
              .endOf("day")
              .format("YYYY-MM-DDTHH:mm:ss"),
          ];
          break;

        case "THIS MONTH":
          res = [
            moment()
              .startOf("month")
              .format("YYYY-MM-DDTHH:mm:ss"),
            moment()
              .endOf("day")
              .format("YYYY-MM-DDTHH:mm:ss"),
          ];
          break;

        case "LAST MONTH":
          res = [
            moment()
              .subtract(1, "months")
              .startOf("month")
              .format("YYYY-MM-DDTHH:mm:ss"),
            moment()
              .subtract(1, "months")
              .endOf("month")
              .format("YYYY-MM-DDTHH:mm:ss"),
          ];
          break;

        default:
          break;
      }

      this.dates = this.localTime
        ? res
        : res.map((i) =>
            moment(i)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss")
          );

      this.startTime = null;
      this.endTime = null;
      this.$emit("change", this.dates);
      this.menu = false;
    },
  },
};
</script>

<style lang="scss">
.date-time-range-picker {
  .v-picker__actions.v-card__actions {
    flex-wrap: wrap;
  }
  .time-range {
    max-width: unset;
  }
  .shortcuts {
    position: absolute;
    top: 120px;
    left: 20px;
    width: 130px;
    height: 280px;
    z-index: 2;
    display: flex;
  }
  .shrinkHeight {
    height: 130px !important;
  }
}
.control-container {
  padding: 0 1em;
}
.white-theme .v-icon {
  color: white;
}
.white-theme input {
  color: white !important;
}
</style>
